import { createRoute, useNavigate } from '@tanstack/react-router';
import { rotaPainel } from './painel';
import { Tela } from '../workspace/Tela';
import { usarPropostas } from '../toolkit/consultas';
import { Abas } from '../toolkit/pecas';
import { AbaRotinas } from '../toolkit/AbaRotinas';
import { AbaDicionario } from '../toolkit/AbaDicionario';
import { AbaAlvos } from '../toolkit/AbaAlvos';

/**
 * A tela do Toolkit (F3.7 da revisão de out/2026, ADR-0026): o catálogo de
 * rotinas validadas por alvo, o dicionário de memória e os alvos — e o lugar
 * onde um humano decide o que a mestre propôs (D19, D20).
 *
 * A aba vive na URL (`?aba=`): o voltar do navegador devolve a aba de onde se
 * veio, e um link para "as propostas do dicionário" é um endereço.
 */

const ABAS = ['rotinas', 'dicionario', 'alvos'] as const;
type Aba = (typeof ABAS)[number];

function Toolkit(): React.JSX.Element {
  const aba = rotaToolkit.useSearch().aba ?? 'rotinas';
  const navegar = useNavigate();
  const propostas = usarPropostas();
  const deRotina = (propostas.data ?? []).filter((p) => p.especie !== 'verbete').length;
  const deVerbete = (propostas.data ?? []).filter((p) => p.especie === 'verbete').length;

  return (
    <Tela>
      <header className="space-y-0.5">
        <h1 className="text-lg font-medium text-zinc-100">Toolkit</h1>
        <p className="text-sm text-zinc-500">
          Os jeitos de fazer que já deram certo, por alvo. A mestre propõe; uma pessoa aprova.
        </p>
      </header>
      <Abas<Aba>
        abas={[
          { id: 'rotinas', rotulo: 'Rotinas', contagem: deRotina },
          { id: 'dicionario', rotulo: 'Dicionário', contagem: deVerbete },
          { id: 'alvos', rotulo: 'Alvos' },
        ]}
        atual={aba}
        aoTrocar={(proxima) => void navegar({ to: '/toolkit', search: { aba: proxima } })}
      />
      <div role="tabpanel" aria-label={aba}>
        {aba === 'rotinas' ? <AbaRotinas /> : aba === 'dicionario' ? <AbaDicionario /> : <AbaAlvos />}
      </div>
    </Tela>
  );
}

export const rotaToolkit = createRoute({
  getParentRoute: () => rotaPainel,
  path: '/toolkit',
  // Opcional: o menu leva a `/toolkit` sem nada, e a aba de partida é Rotinas.
  validateSearch: (busca: Record<string, unknown>): { aba?: Aba } =>
    (ABAS as readonly unknown[]).includes(busca['aba']) ? { aba: busca['aba'] as Aba } : {},
  component: Toolkit,
});
